/**
 * The client half of the push channel: stay connected to `/api/events`, and say what the
 * server said.
 *
 * Written once and shared, so both phones and the browser hear about a change the same way and
 * at the same moment. What differs per platform is only *how bytes arrive* —
 * [ServerEventTransport] — and that is a seam rather than a fork: the reconnect policy, the
 * backoff, the re-read-on-connect rule and the echo filtering are all here, once.
 *
 * ── Re-read on every connect, not only the first ─────────────────────────────
 *
 * The single most important rule in this file, and the one whose absence is invisible. The
 * channel **carries no replay** (see `ApiRoutes.EVENTS`), so a client that was disconnected for
 * a minute missed everything said during it. Reconnecting therefore has to mean "go and read
 * the current truth", not "resume listening" — otherwise a phone that lost signal in a lift
 * comes back believing it is up to date, and stays wrong until somebody happens to change
 * something else.
 *
 * That is what [ServerEventStream.connectionsFlow] is: a counter that increments on every
 * *successful* connect. A consumer treats a bump exactly as it treats an event naming
 * everything it cares about.
 *
 * ── Backoff, and why the ceiling is deliberately low ─────────────────────────
 *
 * A server that is restarting is the ordinary reason to be disconnected, and it is back in a
 * few seconds. A ceiling of half a minute keeps a phone that woke up in a tunnel from waiting
 * minutes after the network returns, and the cost of being wrong in that direction is one
 * request per thirty seconds against a server that serves one person.
 *
 * @see se.soderbjorn.lunapin.clientserver.ServerEvent
 */
package se.soderbjorn.lunapin.client

import io.ktor.client.HttpClient
import io.ktor.client.request.accept
import io.ktor.client.request.prepareGet
import io.ktor.client.statement.bodyAsChannel
import io.ktor.http.ContentType
import io.ktor.http.isSuccess
import io.ktor.utils.io.readUTF8Line
import kotlin.coroutines.cancellation.CancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import se.soderbjorn.lunapin.clientserver.ApiRoutes
import se.soderbjorn.lunapin.clientserver.ServerEvent
import se.soderbjorn.lunapin.clientserver.ServerEventCodec

/** How long to wait before the first reconnect attempt. */
private const val BACKOFF_START_MS = 1_000L

/** The ceiling. See the file header on why it is deliberately low. */
private const val BACKOFF_MAX_MS = 30_000L

/**
 * How bytes get here. One implementation per way of reading a stream.
 *
 * Two exist: [KtorServerEventTransport] for everything that can hold an HTTP response open,
 * and the browser's own `EventSource` over in `:web` — which is not a preference but a
 * requirement, since it is the only carrier in a browser that attaches the session cookie
 * without being asked.
 *
 * An implementation **must not retry**. Returning is how it says the connection ended, and
 * [ServerEventStream] owns what happens next; a transport with a retry loop of its own would be
 * a second backoff policy, and the two would not agree about when to re-read.
 */
interface ServerEventTransport {
    /**
     * Connect, and deliver events until the connection ends.
     *
     * @param onConnected called once, **after** the connection is established and before any
     *   event. This is what drives the re-read; a transport that called it optimistically
     *   before connecting would make every failed attempt look like a reconnection.
     * @param onEvent called per event, in arrival order, on whatever coroutine this is running
     *   on.
     * @throws Exception for a connection that failed or dropped. Returning normally means the
     *   same thing; both are reconnected from.
     */
    @Throws(CancellationException::class, Exception::class)
    suspend fun stream(onConnected: () -> Unit, onEvent: (ServerEvent) -> Unit)
}

/**
 * Keeps one [ServerEventTransport] connected and republishes what it carries.
 *
 * @param transport how to connect.
 * @param originId this client's own id, as stamped on its requests. Events carrying it are
 *   **this client's own writes coming back** and are dropped here rather than at each consumer
 *   — one filter, in the one place that can see every event, instead of the same `if` repeated
 *   at every subscriber with one of them eventually missing.
 */
class ServerEventStream(
    private val transport: ServerEventTransport,
    private val originId: String,
) {
    private val _events = MutableSharedFlow<ServerEvent>(extraBufferCapacity = EVENT_BUFFER)

    /**
     * Everything the server said that this client did not cause.
     *
     * A `SharedFlow` rather than a `StateFlow`: these are events, and two identical ones in a
     * row are two facts. A `StateFlow` would conflate "bookmark 412 changed" with the same
     * sentence a second later, which is exactly the case where the second one matters.
     *
     * Buffered rather than suspending, so a slow subscriber cannot stall the connection.
     * Losing an event is survivable by construction: every event is only ever an instruction to
     * go and read, and the next reconnect re-reads everything regardless.
     */
    val events: SharedFlow<ServerEvent> = _events.asSharedFlow()

    private val _connectionsFlow = MutableStateFlow(0)

    /**
     * How many times this stream has successfully connected.
     *
     * **A bump means "you have missed things; go and read."** Including the first, which is why
     * a consumer needs no separate first-load path. See the file header — this is the mechanism
     * the no-replay decision hangs on.
     */
    val connectionsFlow: StateFlow<Int> = _connectionsFlow.asStateFlow()

    private val _isConnectedFlow = MutableStateFlow(false)

    /**
     * Whether there is a live connection right now.
     *
     * The one thing about freshness still worth saying on screen once the `↻` button is gone.
     * *"REFRESHED 4 MIN AGO"* was answering a question nobody needs answered when updates are
     * continuous; *"not connected"* is genuinely worth knowing, because it is the state in
     * which what you are looking at can silently be out of date.
     */
    val isConnectedFlow: StateFlow<Boolean> = _isConnectedFlow.asStateFlow()

    /**
     * Connect, and keep reconnecting. Called once; never returns.
     *
     * Never throws except for cancellation. A transport that fails is a connection to retry,
     * not an error to report: there is no action a user can take about it that is not already
     * covered by [isConnectedFlow], and a stream that gave up would be one whose absence is
     * discovered hours later.
     */
    @Throws(CancellationException::class)
    suspend fun run() {
        var backoff = BACKOFF_START_MS
        while (true) {
            try {
                transport.stream(
                    onConnected = {
                        backoff = BACKOFF_START_MS
                        _isConnectedFlow.value = true
                        // Bumped on every connect, including reconnects. See the file header.
                        _connectionsFlow.value = _connectionsFlow.value + 1
                    },
                    onEvent = { event ->
                        // The echo filter, in the one place that sees every event. A client
                        // that re-read because of its own write would fight whatever the user
                        // is still doing — mid-drag geometry being the case that shows it.
                        if (event.originId != originId) _events.tryEmit(event)
                    },
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (_: Exception) {
                // Deliberately swallowed, and deliberately not logged per attempt: a phone with
                // no network produces one of these every backoff, forever, and a log line each
                // time is noise that buries the one failure worth reading.
            }
            _isConnectedFlow.value = false
            delay(backoff)
            backoff = (backoff * 2).coerceAtMost(BACKOFF_MAX_MS)
        }
    }

    private companion object {
        /**
         * How far a subscriber may fall behind before events are dropped.
         *
         * Sized for a burst — an import broadcasting once, a backfill queueing four hundred
         * rows — rather than for a backlog, because there is no value in a long queue of
         * "go and read" instructions when the newest one subsumes every older one.
         */
        const val EVENT_BUFFER = 64
    }
}

/**
 * The event stream over an ordinary held-open HTTP response.
 *
 * Used by both phones. Not by the browser — see [ServerEventTransport].
 *
 * ── Parsing SSE by hand, which is less than it sounds ────────────────────────
 *
 * The subset this server speaks is: `data:` lines carrying one JSON event, comment lines
 * beginning with `:` (the heartbeat, which exists to be bytes and means nothing), a `retry:`
 * field the Kotlin clients ignore because they run their own backoff, and a blank line ending
 * each frame. Everything else in the specification — multi-line data, `event:` names,
 * `Last-Event-ID` — this server never sends, and a parser for what is not sent would be
 * untested code guarding against our own future carelessness rather than against anything real.
 *
 * A line this client cannot decode is **skipped rather than fatal** — see `ServerEventCodec`,
 * where the forward-compatibility argument is made: a server newer than this client will
 * eventually send a case it has never heard of, and tearing down a working connection over one
 * unreadable line would lose the events it does understand.
 *
 * @param httpClient the transport. **Its own, not the API's**, and that is the exception
 *   `SyncSetupBackingViewModel`'s factory names in advance — *"the line to revisit is when
 *   something calls the server continuously"*. This is that caller, and it needs a client
 *   configured differently as well as separately: an engine's default read timeout would close
 *   a perfectly healthy idle stream, so it must be built by
 *   [se.soderbjorn.lunapin.clientserver.createEventStreamHttpClient] and not by the ordinary
 *   factory.
 * @param baseUrl the configured server, or `""` for a relative URL.
 */
class KtorServerEventTransport(
    private val httpClient: HttpClient,
    private val baseUrl: String,
) : ServerEventTransport {

    override suspend fun stream(onConnected: () -> Unit, onEvent: (ServerEvent) -> Unit) {
        httpClient.prepareGet("$baseUrl${ApiRoutes.EVENTS}") {
            accept(ContentType.Text.EventStream)
        }.execute { response ->
            // Thrown rather than returned, so the loop above backs off instead of reconnecting
            // immediately against a server that is refusing. A 401 here is the ordinary
            // lapsed-session case and has already been reported by the client's own validator,
            // exactly as it is for every other route.
            if (!response.status.isSuccess()) {
                error("The event stream was refused with ${response.status}.")
            }
            onConnected()
            val channel = response.bodyAsChannel()
            while (true) {
                val line = channel.readUTF8Line() ?: break
                if (!line.startsWith(DATA_PREFIX)) continue
                ServerEventCodec.decode(line.removePrefix(DATA_PREFIX).trim())?.let(onEvent)
            }
        }
    }

    private companion object {
        const val DATA_PREFIX = "data:"
    }
}
